import { UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Checkbox, Collapse, Form, Input, Select, Upload } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AdSizeBean, AdvertiserBean, CreativeBean } from 'src/api/api'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import { enumView } from 'src/util/Util'
import { env } from '../../../env'
import creativeApi from './CreativeApi'
import { getFileLabel, macroItems } from './CreativeCreate'
import './style.css'

const FormItem = Form.Item
const Option = Select.Option
const TextArea = Input.TextArea

const formatEnum = CreativeBean.FormatEnum
const attributeEnum = CreativeBean.AttributeEnum

interface Props { }

const formInfoItems: FormDataType[] = [
    FormDataType.AdSizes,
    FormDataType.Advertisers
]

const CreativeEdit: React.FC<Props> = () => {

    const [form] = useForm()
    const [error, setError] = useState()
    const navigateTo = useNavigate()

    const [iconUrl, setIconUrl] = useState()
    const [iconToUpload, setIconTuUpload] = useState()

    const [fileUrl, setFileUrl] = useState()
    const [fileToUpload, setFileToUpload] = useState()

    const [info, setInfo] = useState({
        adSizes: [],
        advertisers: []
    })

    const [containerWidth, setContainerWidth] = useState(0)
    const [previewNode, setPreviewNode] = useState<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!previewNode) return

        const observer = new ResizeObserver(entries => {
            setContainerWidth(entries[0].contentRect.width)
        })
        observer.observe(previewNode)

        return () => observer.disconnect()
    }, [previewNode])

    const { id } = useParams<{ id: string }>()

    const [params] = useSearchParams()

    const adGroupId = params.get('adGroupId')

    const handleSubmit = async () => {
        const values = await form.validateFields()
        try {
            await creativeApi.edit(id, values, iconToUpload, fileToUpload)
            goBack()
        } catch (e) {
            setError(e.error)
        }
    }

    const loadData = async () => {
        try {
            const response: any = await creativeApi.view(id)
            form.setFieldsValue(response.data)

            setIconUrl(response.data.nativeData?.icon)
            setFileUrl(response.data.nativeData?.banner || response.data.file)

            const formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const goBack = () => navigateTo(adGroupId ? `/ad-group/${adGroupId}/edit` : '/creative')

    const renderIFrame = () => {

        const adSize = (info.adSizes as AdSizeBean[]).find((size: AdSizeBean) => size.id == form.getFieldValue('adSizeId'))
        const nativeWidth = adSize?.width || 300
        const nativeHeight = adSize?.height || 250
        const scale = containerWidth > 0 ? Math.min(1, containerWidth / nativeWidth) : 1

        return <div
            ref={setPreviewNode}
            style={{
                width: '100%',
                height: nativeHeight * scale,
                overflow: 'hidden',
                position: 'relative'
            }}
        >
            <iframe
                src={'' + env.bucket + fileUrl}
                scrolling="no"
                width={nativeWidth}
                height={nativeHeight}
                style={{
                    border: 0,
                    borderRadius: 8,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left'
                }}
            />
        </div>
    }

    const format = Form.useWatch('format', form)

    return <Form
        style={{
            maxWidth: '1000px', margin: '0 auto'
        }}
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 16 }}
    >
        {error ? <Alert message={error} type="error" /> : null}
        <Card title="Creative" size="small" className="form-card">
            <FormItem
                required
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Creative name is required' }]}
            >
                <Input placeholder="Name" />
            </FormItem>
            <FormItem
                required
                name="advertiserId"
                label="Advertiser"
                rules={[{ required: true, message: 'Creative Advertiser ID required' }]}
            >
                <Select showSearch optionFilterProp="title" placeholder="Select Advertiser">
                    {info.advertisers.map((item: AdvertiserBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                name="adSizeId"
                label="Ad Size"
                rules={[{ required: true, message: 'Creative AdSize ID required' }]}
            >
                <Select showSearch optionFilterProp="title" placeholder="Select Ad Size">
                    {info.adSizes.map((item: AdSizeBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                name="format"
                label="Format"
                rules={[{ required: true, message: 'Creative format required' }]}
            >
                <Select placeholder="Select Format">
                    {Object.values(CreativeBean.FormatEnum).map(type => <Option key={type} value={type}>{enumView(type)}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="landingUrl"
                label="Landing URL"
            >
                <Input placeholder="Landing URL" />
            </FormItem>
            <FormItem
                name="attribute"
                label="Attributes"
            >
                <Select mode="multiple" placeholder="Select Attributes" allowClear>
                    {Object.keys(attributeEnum).map(item => <Option key={item} value={item}>{enumView(item)}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="active"
                label="Active"
                valuePropName="checked"
                initialValue="true"
            >
                <Checkbox />
            </FormItem>
            {(format == formatEnum.HTML || format == formatEnum.VAST) && <FormItem
                name="adCode"
                label="Ad Code"
            >
                <TextArea rows={4} />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'title']}
                label="Title"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'description']}
                label="Description"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'cta']}
                label="Call to Action"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'sponsored']}
                label="Sponsored by"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                label="Icon"
            >
                <Upload
                    maxCount={1}
                    beforeUpload={(incomingFile: any) => {
                        setIconTuUpload(incomingFile)
                        return false
                    }}
                >
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
            </FormItem>}
            {format == formatEnum.Native && iconUrl && <FormItem
                wrapperCol={{ offset: 5, span: 16 }}
            >
                <div className="icon-preview">
                    <img src={'' + env.bucket + iconUrl} />
                </div>
            </FormItem>}
            <FormItem
                label={getFileLabel(format)}
            >
                <Upload
                    maxCount={1}
                    beforeUpload={async (incomingFile: any) => {
                        setFileToUpload(incomingFile)

                        const blobUrl = URL.createObjectURL(incomingFile)
                        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                            const i = new Image()
                            i.onload = () => resolve(i)
                            i.onerror = reject
                            i.src = blobUrl
                        })
                        const { width: w, height: h } = img

                        const exist = (info.adSizes as AdSizeBean[]).find((size: AdSizeBean) => size.width == w && size.height == h)
                        if (exist) {
                            form.setFieldValue('adSizeId', exist.id)
                        }

                        return false
                    }}
                >
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
            </FormItem>
            {fileUrl && <FormItem
                wrapperCol={{ offset: 5, span: 16 }}
            >
                <div className="creative-preview">
                    {format == formatEnum.GoogleHTML5
                        ? renderIFrame()
                        : <img src={'' + env.bucket + fileUrl} />}
                </div>
            </FormItem>}
            {(format == formatEnum.HTML || format == formatEnum.VAST) && <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                <Collapse items={macroItems} size="small" className="macro-guide" />
            </FormItem>}
        </Card>
        <div className="create-cancel-group">
            <Button type="primary" htmlType="submit">Save</Button>
            <Button htmlType="button" danger onClick={goBack}>Cancel</Button>
        </div>
    </Form>
}

export default CreativeEdit


// WEBPACK FOOTER //
// ./src/component/app/creative/CreativeEdit.tsx